import { create } from 'zustand';
import { toast } from 'sonner';
import { useUIStore } from './uiStore';
import { useEmailStore } from './emailStore';
import type { ReplyContext } from '@/types/ui';
import type { ComposeAttachment } from '@/types/compose';

const UNDO_SEND_DELAY_MS = 10_000;

export interface PendingSendPayload {
  accountId: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  html: string;
  text: string;
  attachments?: File[];
  /** Server-side attachment ids re-resolved at send time (forwards/drafts). */
  retainedAttachmentIds?: string[];
  threadId?: string | null;
  inReplyTo?: string | null;
  references?: string | null;
  idempotencyKey: string;
  draftId?: string | null;
  showCc: boolean;
  showBcc: boolean;
  // For restoring compose state on undo
  replyContext?: ReplyContext | null;
  composeAttachments?: ComposeAttachment[];
  selectedSignatureId?: string | null;
}

interface PendingSend {
  id: string;
  payload: PendingSendPayload;
  timer: ReturnType<typeof setTimeout>;
  toastId: string | number;
  /** Once the undo window expires and executeSend STARTS, undo is impossible. */
  executing: boolean;
}

interface PendingSendState {
  pending: Map<string, PendingSend>;
  enqueue: (
    payload: PendingSendPayload,
    executeSend: (payload: PendingSendPayload) => Promise<void>,
  ) => string;
  cancel: (id: string) => void;
  remove: (id: string) => void;
}

export const usePendingSendStore = create<PendingSendState>((set, get) => ({
  pending: new Map(),

  enqueue: (payload, executeSend) => {
    const id = crypto.randomUUID();

    const timer = setTimeout(async () => {
      // Timer expired — actually send. The entry is kept (marked executing)
      // until executeSend has STARTED, so a refresh inside the undo window can
      // flush it via keepalive, and remove() only runs once we own the send.
      const entry = get().pending.get(id);
      if (!entry) return;
      set((state) => {
        const next = new Map(state.pending);
        const current = next.get(id);
        if (current) {
          next.set(id, { ...current, executing: true });
        }
        return { pending: next };
      });
      get().remove(id);
      try {
        await executeSend(payload);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to send email');
        // Remove the optimistic reply so a failed send (e.g. an unresolvable
        // forwarded attachment) doesn't leave a ghost message in the thread.
        if (payload.idempotencyKey) {
          useEmailStore
            .getState()
            .removeOptimisticThreadMessage(`optimistic:${payload.idempotencyKey}`);
        }
        // Reopen compose on send failure
        reopenCompose(payload);
      }
    }, UNDO_SEND_DELAY_MS);

    const toastId = toast('Email will be sent', {
      // The toast ends WITH the undo window — an Undo button that outlives the
      // window was a visible-but-dead control (G17).
      duration: UNDO_SEND_DELAY_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          get().cancel(id);
        },
      },
    });

    const entry: PendingSend = { id, payload, timer, toastId, executing: false };
    set((state) => {
      const next = new Map(state.pending);
      next.set(id, entry);
      return { pending: next };
    });

    return id;
  },

  cancel: (id) => {
    const entry = get().pending.get(id);
    if (!entry) return;
    if (entry.executing) {
      // The window expired a beat ago and the send is in flight — undo is no
      // longer possible; say so instead of silently no-opping (G17).
      toast.error('Too late to undo — the email is being sent');
      return;
    }
    clearTimeout(entry.timer);
    toast.dismiss(entry.toastId);
    set((state) => {
      const next = new Map(state.pending);
      next.delete(id);
      return { pending: next };
    });
    // Remove the optimistic reply we appended to the thread at send time so a
    // cancelled send doesn't leave a ghost message in the conversation.
    if (entry.payload.idempotencyKey) {
      useEmailStore
        .getState()
        .removeOptimisticThreadMessage(`optimistic:${entry.payload.idempotencyKey}`);
    }
    // Reopen compose with preserved state
    reopenCompose(entry.payload);
    toast.success('Send cancelled');
  },

  remove: (id) => {
    const entry = get().pending.get(id);
    if (!entry) return;
    toast.dismiss(entry.toastId);
    set((state) => {
      const next = new Map(state.pending);
      next.delete(id);
      return { pending: next };
    });
  },
}));

// ---------------------------------------------------------------------------
// Page-unload flush (G3): a send living only in a 10s setTimeout silently
// dies with the page. On pagehide, fire each not-yet-executing pending send
// immediately via fetch(keepalive) so the email still goes out. (Durable
// design — a server-side undo window via scheduledAt+cancel — is recorded as
// the needs-backend follow-up; this keepalive flush ships now.) Limitation:
// keepalive bodies are capped (~64KiB), so pending sends with large bodies or
// fresh File attachments may not flush — those need the server-side window.
// ---------------------------------------------------------------------------

function keepaliveSend(payload: PendingSendPayload): void {
  const token = localStorage.getItem('authToken');
  if (!token) return;
  const body = JSON.stringify({
    to: payload.to,
    cc: payload.cc,
    bcc: payload.bcc,
    subject: payload.subject,
    html: payload.html,
    text: payload.text,
    threadId: payload.threadId ?? undefined,
    inReplyTo: payload.inReplyTo ?? undefined,
    references: payload.references ?? undefined,
    // Without these a flushed forward/draft send arrives attachment-less (N7).
    retainedAttachmentIds: payload.retainedAttachmentIds ?? undefined,
    idempotencyKey: payload.idempotencyKey,
    draftId: payload.draftId ?? undefined,
  });
  fetch(`/api/emails/send?account_id=${encodeURIComponent(payload.accountId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-access-token': token,
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Best-effort: the page is going away.
  });
}

function flushPendingSendsOnUnload(): void {
  const { pending } = usePendingSendStore.getState();
  pending.forEach((entry) => {
    if (entry.executing) return;
    clearTimeout(entry.timer);
    // Dismiss the toast too (N26): a bfcache restore resurrects the DOM as-is,
    // and a live-looking Undo button for an already-flushed send is a lie.
    toast.dismiss(entry.toastId);
    keepaliveSend(entry.payload);
  });
  if (pending.size > 0) {
    usePendingSendStore.setState({ pending: new Map() });
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPendingSendsOnUnload);
  window.addEventListener('beforeunload', flushPendingSendsOnUnload);
}

// ---------------------------------------------------------------------------
// Reopen queue (G18): two failed/undone sends both want the composer. The
// composer is a single surface — reopen the first immediately and queue the
// rest; each close pops the next instead of the last payload clobbering the
// earlier ones.
// ---------------------------------------------------------------------------

const reopenQueue: PendingSendPayload[] = [];
let reopenWatcherArmed = false;

function armReopenWatcher() {
  if (reopenWatcherArmed) return;
  reopenWatcherArmed = true;
  const unsubscribe = useUIStore.subscribe((state, previous) => {
    if (previous.composeOpen && !state.composeOpen && reopenQueue.length > 0) {
      const nextPayload = reopenQueue.shift()!;
      // Defer a tick so the closing dialog finishes unmounting first.
      setTimeout(() => {
        // The user may have opened a NEW compose inside this tick (N26) —
        // re-queue instead of clobbering it.
        if (useUIStore.getState().composeOpen) {
          reopenQueue.unshift(nextPayload);
          armReopenWatcher();
          return;
        }
        openComposeForPayload(nextPayload);
      }, 50);
      if (reopenQueue.length === 0) {
        reopenWatcherArmed = false;
        unsubscribe();
      }
    }
  });
}

function openComposeForPayload(payload: PendingSendPayload) {
  // Carry the FULL session identity back into the composer: the sending
  // account and the existing draft id — a reopened compose must send from the
  // same account and keep UPDATING the same draft, not fork a duplicate (G2).
  const expandState = {
    attachments: payload.composeAttachments,
    selectedSignatureId: payload.selectedSignatureId,
    showBcc: payload.showBcc,
    bcc: payload.bcc,
    // Reuse the same idempotency key on resend so a send that actually
    // reached the server (then appeared to fail) is deduped rather than
    // delivered twice (audit §2.9 #6).
    idempotencyKey: payload.idempotencyKey,
    draftId: payload.draftId ?? null,
    fromAccountId: payload.accountId,
  };
  const replyContext: ReplyContext = payload.replyContext
    ? {
        ...payload.replyContext,
        accountId: payload.accountId,
        // The payload's recipients/subject are the user's SEND-TIME values —
        // they must win over the original reply seed (N6: reopening after
        // undo reverted edited To/Cc/Subject, and because the session keeps
        // the same draft id, the next autosave persisted the reversion over
        // the server draft).
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        // Send-time subject is already final — no re-prefixing (N26).
        skipSubjectPrefix: true,
        // Carry the composed content as draftBody so it's restored in the editor
        draftBody: payload.html,
        _expandState: expandState,
      }
    : {
        accountId: payload.accountId,
        to: payload.to,
        subject: payload.subject,
        // A fresh compose has no reply seed — without this it reopened with a
        // spurious "Re: " prefix (N26).
        skipSubjectPrefix: true,
        draftBody: payload.html,
        mode: 'reply' as const,
        cc: payload.cc,
        _expandState: expandState,
      };

  useUIStore.setState({
    composeOpen: true,
    replyContext: replyContext,
    draftEmail: null,
  });
}

function reopenCompose(payload: PendingSendPayload) {
  const { composeOpen } = useUIStore.getState();
  if (composeOpen) {
    // A composer is already on screen (e.g. another reopened send) — queue
    // this payload instead of clobbering it (G18).
    reopenQueue.push(payload);
    armReopenWatcher();
    toast('A cancelled send is queued — it will reopen when the current composer closes');
    return;
  }
  openComposeForPayload(payload);
}
